import { Node, mergeAttributes } from '@tiptap/core';
import { VueNodeViewRenderer } from '@tiptap/vue-3';
import ExtractionTableNodeView from '../../components/editor/ExtractionTableNodeView.vue';

// ── Snapshot shape (mirrors ExtractionSnapshotController response) ─────

export interface SnapshotColumn {
    id: string;
    name: string;
    type: string;
}

export interface SnapshotCell {
    value: string | null;
    status: string | null;
}

export interface SnapshotRow {
    paperId: string;
    paperLabel: string;
    cells: Record<string, SnapshotCell>;
}

export interface ExtractionSnapshot {
    gridId: string;
    columns: SnapshotColumn[];
    rows: SnapshotRow[];
    snapshotAt: string;
}

// ── TipTap command type extension ─────────────────────────────────────

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        extractionTable: {
            /**
             * Insert a frozen extraction-table snapshot at the current position.
             */
            insertExtractionTable: (options: {
                gridId: string;
                columnIds: string[];
                filter: 'all' | 'validated';
                caption: string;
                snapshotData: ExtractionSnapshot;
            }) => ReturnType;
        };
    }
}

/**
 * Block atom node that embeds a frozen snapshot of an extraction grid.
 *
 * The snapshot is serialised as JSON inside the `snapshotData` attribute so
 * the document is self-contained — reloading or sharing it does not require
 * a live DB connection.
 *
 * Unified "Tableau N" counter (same sequence as NumberedTableExtension).
 */
export const ExtractionTableExtension = Node.create({
    name: 'extractionTable',

    group: 'block',

    atom: true,

    draggable: true,

    addAttributes() {
        return {
            /** Stable UUID generated at insertion time (used by the counter). */
            tableId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-table-id'),
                renderHTML: (attrs) => ({ 'data-table-id': attrs.tableId }),
            },
            gridId: {
                default: null,
                parseHTML: (el) => el.getAttribute('data-grid-id'),
                renderHTML: (attrs) => ({ 'data-grid-id': attrs.gridId }),
            },
            /** JSON array of column IDs selected at insertion time. */
            columnIds: {
                default: [],
                parseHTML: (el) => {
                    try {
                        return JSON.parse(
                            el.getAttribute('data-column-ids') ?? '[]',
                        );
                    } catch {
                        return [];
                    }
                },
                renderHTML: (attrs) => ({
                    'data-column-ids': JSON.stringify(attrs.columnIds),
                }),
            },
            filter: {
                default: 'all',
                parseHTML: (el) => el.getAttribute('data-filter') ?? 'all',
                renderHTML: (attrs) => ({ 'data-filter': attrs.filter }),
            },
            caption: {
                default: '',
                parseHTML: (el) =>
                    el.querySelector('[data-caption]')?.textContent ?? '',
                renderHTML: () => ({}),
            },
            /** Frozen snapshot serialised as JSON. */
            snapshotData: {
                default: null,
                parseHTML: (el) => {
                    try {
                        return JSON.parse(
                            el.getAttribute('data-snapshot') ?? 'null',
                        );
                    } catch {
                        return null;
                    }
                },
                renderHTML: (attrs) => ({
                    'data-snapshot': attrs.snapshotData
                        ? JSON.stringify(attrs.snapshotData)
                        : '',
                }),
            },
        };
    },

    parseHTML() {
        return [{ tag: 'div[data-table-id][data-grid-id]' }];
    },

    renderHTML({ HTMLAttributes }) {
        return [
            'div',
            mergeAttributes(HTMLAttributes, { 'data-extraction-table': '1' }),
            0,
        ];
    },

    addNodeView() {
        return VueNodeViewRenderer(ExtractionTableNodeView);
    },

    addCommands() {
        return {
            insertExtractionTable:
                ({ gridId, columnIds, filter, caption, snapshotData }) =>
                ({ commands }) => {
                    const tableId =
                        typeof crypto !== 'undefined' && crypto.randomUUID
                            ? crypto.randomUUID()
                            : Math.random().toString(36).slice(2);

                    return commands.insertContent({
                        type: 'extractionTable',
                        attrs: {
                            tableId,
                            gridId,
                            columnIds,
                            filter,
                            caption,
                            snapshotData,
                        },
                    });
                },
        };
    },
});
